import {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
} from "react"
import { classNames } from "../../_utils/component"

export type VariantType = "primary" | "secondary" | "danger"
type LayoutType = "hug" | "fill"

type ButtonBaseProps = {
  children: ReactNode
  variant?: VariantType
  layout?: LayoutType
  disabled?: boolean
  onClick?: MouseEventHandler
}

type ButtonProps = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement>

export const Button = forwardRef(
  (
    {
      children,
      variant = "primary",
      layout = "hug",
      disabled = false,
      onClick = undefined,
      ...rest
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const getClassName = useCallback(
      (variant: VariantType, layout: LayoutType, disabled: boolean) => {
        const layoutClassName = LAYOUT_CLASS_NAMES[layout]
        if (disabled) {
          return classNames(BASE_CLASS_NAME, layoutClassName)
        }
        return classNames(
          BASE_CLASS_NAME,
          layoutClassName,
          VARIANT_CLASS_NAMES[variant]
        )
      },
      []
    )

    return (
      <button
        className={getClassName(variant, layout, disabled)}
        disabled={disabled}
        aria-disabled={disabled}
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = "Button"

const BASE_CLASS_NAME =
  "justify-center text-sm font-bold leading-5" +
  " box-border border rounded-sm" +
  " focus:outline-none focus:ring-2 focus:ring-offset-2" +
  " disabled:bg-itc-gray disabled:border-itc-gray disabled:text-white"

const LAYOUT_CLASS_NAMES: { [key in LayoutType]: string } = {
  hug: "px-5 py-3",
  fill: "w-full py-3",
} as const

const VARIANT_CLASS_NAMES: { [key in VariantType]: string } = {
  primary: "text-white bg-itc-green border-itc-green",
  secondary: "text-itc-green bg-white border-itc-green",
  danger: "text-white bg-itc-red border-itc-red",
} as const
